import type {
  Optional,
  Resume,
  UserDocumentTemplate,
  ResumeAvatarTransform,
  DateRangePickerValue,
  EnumerableResumeSectionType,
  UserDocumentTemplateId,
  TemplateSettings,
} from '@rio/types'
import { Step as JoyRideStep } from 'react-joyride'
import { TABS } from 'builder/components/FillResumeModal/constants'
import {
  AIResumeLinkedInSteps,
  AIResumeFlows,
  FetchStatuses,
  ResumeUploadStep,
  ResumeUploadStatus,
  ResumeValidationStatus,
  GenerateResumeStatus,
  SpeechToTextStatus,
  AIResumeWarningModalActions,
  AIResumeWarningModalTypes,
  VoiceInputErrors,
  GenerateAiSummaryStatus,
  AiSummaryGenerateType,
  FORMATS,
} from 'builder/modules/constants'
import { UseExample } from 'builder/components/FillResumeModal/UseExample/types'
import { SelectValue } from 'builder/components/Select/types'
import { LanguageLevelData, SkillLevelData } from '../generalEditor'
import { DownloadTypes } from '../rendering'
import { SectionNames } from './sectionNames'

export type Step = {
  id: string
  translationKey: string
  done: boolean
  simpleFieldNames?: string[]
  cardFieldNames?: string[]
}

export enum PrefillStatuses {
  /** initial value */
  notAsked = 'notAsked',
  /** resume doesn't need to prefill */
  skipped = 'skipped',
  /** resume is prefilling */
  scheduled = 'scheduled',
  /** resume prefilled and ready to merge */
  readyToMerge = 'readyToMerge',
  /** when accept of merge was failed */
  mergeFailed = 'mergeFailed',
  /** when reject of merge was failed */
  rejectFailed = 'rejectFailed',
  /** when resume was not be changed and was merged automatically */
  mergedAuto = 'mergedAuto',

  error = 'error',
  failed = 'failed',
  done = 'done',
  rejected = 'rejected',
  merged = 'merged',
}

export enum PrefillProviders {
  topresume = 'topresume',
}

export type ResumeScoreAPIData = {
  jobPostingId: number
  employerName: string
  suggestedJobTitle: string
  averageScore: number
  keywords: string[]
}

export type JobPostingAPIData = {
  jobPostingLink: string
  jobPostingId: number
  isManagementJob: boolean
  employerName: Optional<string>
  recommendedJobTitle: Optional<string>
  visibleJobTitle: string
  recommendedSkills: string[]
  visibleSkills: string[]
  resumeScore: number
  apiScore: number
  averageScore: number
  keywords: string[]
  recommendedKeywords: string[]
}

export type JobPostingManualInputValues = {
  companyName: string
  jobTitle: string
  role: string
}

export type ResumeOptimizerSection = {
  jobPostingLinkForModal: string
  fetchScoreStatus: FetchStatuses
  saveJobPostingDataStatus: FetchStatuses
  fetchJobPostingDataStatus: FetchStatuses
  deleteJobPostingStatus: FetchStatuses
  editJobDetailsStatus: FetchStatuses
  showManualInputScreen: boolean
  temporaryJobPostingAPIData: JobPostingAPIData
  resumeScoreAPIData: ResumeScoreAPIData
  jobPostingAPIData: JobPostingAPIData
  manualInput: JobPostingManualInputValues
  bannerTriggeredFrom: OptimizerBannerTriggerSource | null
}

export type PrefillResumeSection = {
  resumeValidationStatus: ResumeValidationStatus
  selectedResume: File | null
  isSavingPrefilledResume: boolean
  validationError: string | null
  resumeUploadStatus: ResumeUploadStatus
  error: Error | string | null
  errorSource: ResumeUploadStep | null
}

export type AICoverLetter = {
  isEditJobDetailsFocused: boolean
  isClickedOnAiCVEditJobDetails: boolean
  isClickedOnAiCVEditModalIcon: boolean
  isOpenAICVOptimizerModal: boolean
  isShowUntailoreView: boolean
  generateAICoverLetterStatus: FetchStatuses
  aICVEditedJobTitle: string
  aICVEditedCompanyName: string
}

export type AISummary = {
  isModalOpen: boolean
  isResultModalOpen: boolean
  isWithTailoredResumeModalOpen: boolean
  generateAiSummaryStatus: GenerateAiSummaryStatus
  summary: string | null
  actionType: AiSummaryGenerateType | null
  shouldAnimateResultPopupContent: boolean
  shouldAnimateEditorFieldContent: boolean
  includeJobPosting: boolean
}

export type AIResume = {
  showAIResumeModal: boolean
  aiResumeFlow: AIResumeFlows
  hideLinkedInFlow: boolean
  linkedInData: LinkedInData
  questionnaireData: QuestionnaireData
  generateResumeStatus: GenerateResumeStatus
  showWarningModal: boolean
  warningModalType: AIResumeWarningModalTypes | null
  warningModalAction: AIResumeWarningModalActions | null
}

export enum CreateResumeWithLinkedInSteps {
  linkedInURL = 'linkedInURL',
  linkedInProfile = 'linkedInProfile',
}

export type CreateResumeWithLinkedInData = {
  fullName: string | null
  position: string | null
  avatar: string | null
}

export type CreateResumeWithLinkedIn = {
  showCreateResumeWithLinkedInModal: boolean
  currentStep: CreateResumeWithLinkedInSteps
  fetchLinkedInProfileStatus: FetchStatuses
  linkedInProfileURL: string
  linkedInProfileData: CreateResumeWithLinkedInData | null
  errorState: boolean
  updateResumeWithLinkedInStatus: FetchStatuses
}

export type StepByStepFlow = {
  shouldRedirectToAdditionalStep: boolean
}

export type LinkedInProfileData = {
  fullName: string | null
  position: string | null
  avatar: string | null
}

export type LinkedInData = {
  currentStep: AIResumeLinkedInSteps
  linkedInProfileURL: string
  fetchingLinkedInProfile: boolean
  linkedInProfileData: LinkedInProfileData
}

export type QuestionnaireData = {
  step: number
  progress: number
  accomplishmentsVoiceProcessingStatus: SpeechToTextStatus
  accomplishmentsVoiceInputError: VoiceInputErrors | null
  activeAccomplishmentsTab: TABS
  accomplishmentsAudio: Blob[]
  accomplishmentsAudioDuration: number
  goalsVoiceProcessingStatus: SpeechToTextStatus
  goalsVoiceInputError: VoiceInputErrors | null
  activeGoalsTab: TABS
  goalsAudio: Blob[]
  goalsAudioDuration: number
  userInput: {
    workExperience: {
      cards: AIResumeWorkExpCardData[]
      confirmDeleteCardId: string
    }
    desiredPosition: string
    education: {
      cards: AIResumeEducationCardData[]
    }
    currentSkills: string[]
    suggestedSkillsSource: string[]
    suggestedSkills: string[]
    pastAccomplishments: string
    futureGoals: string
  }
}

export type AIResumeWorkExpCardDataError = {
  position: boolean
  company: boolean
  dateFrom: boolean
  dateUntil: boolean
}

export type AIResumeWorkExpCardData = {
  id: string
  position: string
  company: string
  date: DateRangePickerValue
  errors?: AIResumeWorkExpCardDataError
  isCollapsed: boolean
}

export type AIResumeEducationCardData = {
  id: string
  institute: string
  degree: string
  date: DateRangePickerValue
  isCollapsed: boolean
}

export interface ResumeEventTrackedSectionsType {
  [resumeId: number]: string[]
}

export type OpenedSectionsMap = {
  [key in SectionNames]?: boolean
} & {
  [key in ResumeSectionId]?: boolean
}

export type ResumeEditorStore = {
  isLoading: boolean
  isSyncing: boolean
  isUploadingPhoto: boolean
  resume: Resume | null
  currentWizardStepId: string
  wizardSteps: Step[]
  levels: {
    language: LanguageLevelData[]
    skill: SkillLevelData[]
  } | null
  templates: UserDocumentTemplate[]
  error: Error | null
  openedCard: ResumeOpenedCard | null
  openedSections: OpenedSectionsMap
  prefill: {
    status: PrefillStatuses
    provider: PrefillProviders
  }
  isOptimizerModalOpen: boolean
  editJobPosting: boolean
  openedAISuggestionsPopup: Optional<AvailableAISuggestions>
  optimizerSection: ResumeOptimizerSection
  prefillResumeSection: PrefillResumeSection
  isEditJobDetails: boolean
  showToolTip: boolean
  companyName: string
  jobTitle: string
  isClickedOnDeleteJobPosting: boolean
  isClickedOnEditJobDetails: boolean
  changeJobPostingLink: boolean
  isFromJobTracker: boolean
  isOpenSavingInfoBanner: boolean
  isClickedOnAiCVBanner: boolean
  AICoverLetter: AICoverLetter
  aiResume: AIResume
  viewGenerateCLBannerEventTracked: boolean
  useExample: UseExample
  viewTailoringBannerEventTracked: boolean
  aiProfile: AISummary
  highlightedElementData: HighlightedElementData | null
  openedKeywordsModalType: SectionNames | null
  improveResumePanel: {
    isOpen: boolean
    isBadgeAnimating: boolean
  }
  autoTailoring: {
    fetchStatus: FetchStatuses
    openedModal: AutoTailoringOpenedModalType | null
    isApproveModalOpen: boolean
    autoTailoredResume: Resume | null
    jobPostingData: ResumeScoreAPIData | null
    keywordsMap: Record<string, boolean>
    autoTailoredResumeApproveStatus: FetchStatuses
    isFeaturingAutoTailorModalOpenOnDownload: boolean
  }
  originalResume: Resume | null
  resumeEventTrackedSections: ResumeEventTrackedSectionsType
  resumeDownloadData: DownloadTypes
  isPageScrolled: boolean
  aiWorkExperience: AISummary
  aiGenerationPaywallType: AiGenerationPaywallType | null
  createResumeWithLinkedIn: CreateResumeWithLinkedIn
  isCustomizeModalOpen: boolean
  stepByStepFlow: StepByStepFlow
}

export interface AvatarUploadSimplePayload {
  file: File
}

export interface AvatarUploadEditorPayload {
  file?: Optional<File>
  processedFile: File
  transform: ResumeAvatarTransform
}

export type AvatarUploadPayload = AvatarUploadSimplePayload | AvatarUploadEditorPayload

export interface VoiceInputTabProps {
  audioChunks: Array<Blob>
  duration: number
  errorWhileRecording: string
  isRecording: boolean
  onClearRecording: () => void
  onStopRecording: () => void
}

export type HighlightedElementData = Pick<
  JoyRideStep,
  'title' | 'content' | 'disableScrollParentFix' | 'styles'
> & {
  target: string
  buttonLabel?: string
}

export enum AvailableAISuggestions {
  jobTitle = 'jobTitle',
  skills = 'skills',
}

export type AutoTailoringOpenedModalType =
  | 'keywords_selection'
  | 'auto_tailoring'
  | 'tailored_resume_overview'

export type ResumeOpenedCard = {
  id: Optional<number | string>
  sectionId: ResumeSectionId
}

export enum TemplateCategories {
  all = 'all',
  simple = 'simple',
  ats = 'ats',
  professional = 'professional',
  creative = 'creative',
  modern = 'modern',
  free = 'free',
}

export type OptimizerBannerTriggerSource = 'profile' | 'workExperiences'

export type ResumeSectionId = EnumerableResumeSectionType | 'accomplishments' | number

export type UpdateSimpleFieldFunc = (field: any, value: any, debounce?: boolean) => void

export type AiGenerationPaywallType = 'cover_letter' | 'profile' | 'work_experience'

export type TemplateChangeHandler = (id: UserDocumentTemplateId) => void

export type FontChangeHandler = (params: {
  value: SelectValue
  newFontType: 'primaryFont' | 'secondaryFont' | 'tertiaryFont'
  selectedPrimaryFont: SelectValue
  selectedSecondaryFont: SelectValue
  selectedTertiaryFont: SelectValue
}) => void

export type SizeChangeHandler = (newSize: number, oldSize: number) => void

export type SpacingChangeHandler = (newSize: number, oldSize: number) => void

export type LayoutFormatChangeHandler = (value: SelectValue) => void

export type ColorChangeHandler = (color: Optional<string>) => void

export type DownloadDocumentHandler = (params: { format?: FORMATS }) => void

export type UpdateTemplateSettingsParams<
  T extends UserDocumentTemplateId,
  S extends keyof TemplateSettings[T],
> = {
  templateName?: T
  settingsName: S
  value: TemplateSettings[T][S]
}
